import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/errorTracking";
import { generateUUID } from "@/lib/uuid";

// Get current authenticated user ID (if any)
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  } catch {
    return null;
  }
}

// Privacy-minimal mode: only record page_path + entity ids.
// (No page_url/referrer/utm/device fingerprinting/geo.)
function getPageContextMinimal() {
  try {
    const url = new URL(window.location.href);
    return {
      page_path: url.pathname,
    };
  } catch {
    return {
      page_path: window.location.pathname,
    };
  }
}

// Privacy-minimal attribution:
// include referrer + utm_* ONLY once per session (first page_view), to keep source insights working
// without attaching marketing metadata to every granular event.
function getAttributionOncePerSession() {
  try {
    const sentKey = "guest_attribution_sent";
    if (sessionStorage.getItem(sentKey) === "1") return {};

    const url = new URL(window.location.href);
    const utm_source = url.searchParams.get("utm_source") || null;
    const utm_medium = url.searchParams.get("utm_medium") || null;
    const utm_campaign = url.searchParams.get("utm_campaign") || null;
    const utm_term = url.searchParams.get("utm_term") || null;
    const utm_content = url.searchParams.get("utm_content") || null;

    // Only include a referrer when it's present; keep it simple.
    const referrer = document.referrer ? String(document.referrer) : null;

    sessionStorage.setItem(sentKey, "1");
    return {
      referrer,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
    };
  } catch {
    return {};
  }
}

function getTimezoneMinimal() {
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
    };
  } catch {
    return { timezone: null };
  }
}

function getDeviceContext() {
  try {
    const nav: any = navigator;
    const conn: any = (nav && (nav.connection || nav.mozConnection || nav.webkitConnection)) || null;
    const prefersReducedMotion = (() => {
      try {
        return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      } catch {
        return null;
      }
    })();
    const colorScheme = (() => {
      try {
        if (!window.matchMedia) return null;
        if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
        if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
        return null;
      } catch {
        return null;
      }
    })();

    return {
      user_agent: nav?.userAgent ? String(nav.userAgent) : null,
      screen_resolution: window?.screen ? `${window.screen.width}x${window.screen.height}` : null,
      viewport_size:
        typeof window !== "undefined" && window.innerWidth && window.innerHeight
          ? `${window.innerWidth}x${window.innerHeight}`
          : null,
      language: nav?.language ? String(nav.language) : null,
      platform: nav?.platform ? String(nav.platform) : null,
      color_scheme: colorScheme,
      reduced_motion: prefersReducedMotion,
      connection_effective_type: conn?.effectiveType ? String(conn.effectiveType) : null,
      device_memory_gb: typeof nav?.deviceMemory === "number" ? nav.deviceMemory : null,
      hardware_concurrency: typeof nav?.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
    };
  } catch {
    return {};
  }
}
// generateUUID is now imported from @/lib/uuid

// Persistent visitor ID - survives browser restarts (localStorage)
const VISITOR_ID_KEY = "guest_visitor_id";

function getVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateUUID();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  } catch {
    // Fallback if localStorage is blocked - use session-only
    return generateUUID();
  }
}

// Check if visitor is returning (has existing visitor ID)
export function isReturningVisitor(): boolean {
  try {
    return localStorage.getItem(VISITOR_ID_KEY) !== null;
  } catch {
    return false;
  }
}

// Get the current visitor ID (for external use)
export function getGuestVisitorId(): string {
  return getVisitorId();
}

function getSessionId() {
  let sessionId = sessionStorage.getItem("guest_session_id");
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem("guest_session_id", sessionId);
  }
  return sessionId;
}

// Generate a new session ID (used for quiz reset to treat as new user)
export function resetSessionId(): string {
  const newSessionId = generateUUID();
  sessionStorage.setItem("guest_session_id", newSessionId);
  // Clear attribution flag so new session gets fresh attribution
  sessionStorage.removeItem("guest_attribution_sent");
  return newSessionId;
}

// Rate limiting: track last activity time to prevent spam
const lastActivityTime: Record<string, number> = {};
const RATE_LIMIT_MS = 2000; // 2 seconds minimum between activities of the same type (increased from 1s)

// Validate activity type against allowed values
const VALID_ACTIVITY_TYPES = [
  // Views
  "page_view",
  "course_view",
  "chapter_start",
  "card_view",
  // Completions
  "card_complete",
  "chapter_complete",
  "course_complete",
  "course_complete_named", // Completion with learner name (for certificates)
  // News engagement
  "news_view",
  "news_scroll",
  "news_click",
  // Search
  "search",
  // Quiz (home page)
  "quiz_answer",
  "quiz_complete",
  "quiz_to_learn", // Click from quiz to learning page
  // Quiz & Learn
  "quiz_learn_start",
  "quiz_learn_answer",
  "quiz_learn_complete",
  // What Would You Do (scenarios)
  "scenario_vote",
  "endless_quiz_start",
  "endless_quiz_answer",
  "endless_quiz_pause",
  "endless_quiz_resume",
  "endless_quiz_milestone",
  // Feedback
  "feedback_presented", // Modal shown to user
  // Ads
  "ad_impression", // Ad was displayed
  // Badge views
  "badge_view",
] as const;
type ActivityType = typeof VALID_ACTIVITY_TYPES[number];

function isValidActivityType(type: string): type is ActivityType {
  return VALID_ACTIVITY_TYPES.includes(type as ActivityType);
}

// Debounce tracking to avoid rapid-fire calls
let trackingQueue: Array<{
  activityType: ActivityType;
  options: {
    flashcard_id?: string;
    chapter_id?: string;
    course_id?: string;
    search_query?: string;
    metadata?: Record<string, unknown>;
  };
}> = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

async function flushTrackingQueue() {
  if (trackingQueue.length === 0) return;
  
  const itemsToProcess = [...trackingQueue];
  trackingQueue = [];
  
  // Process only the most recent activity per type+resource
  const uniqueActivities = new Map<string, typeof itemsToProcess[0]>();
  itemsToProcess.forEach(item => {
    const key = `${item.activityType}:${item.options.flashcard_id || item.options.chapter_id || item.options.course_id || 'none'}`;
    uniqueActivities.set(key, item);
  });

  const sessionId = getSessionId();
  const visitorId = getVisitorId();
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(sessionId)) return;

  const pageContext = getPageContextMinimal();
  const tz = getTimezoneMinimal();
  const device = getDeviceContext();
  
  // Get current user_id if authenticated
  const userId = await getCurrentUserId();

  const inserts = Array.from(uniqueActivities.values()).map(({ activityType, options }) => ({
    session_id: sessionId,
    visitor_id: visitorId,
    activity_type: activityType,
    flashcard_id: options.flashcard_id || null,
    chapter_id: options.chapter_id || null,
    course_id: options.course_id || null,
    search_query: options.search_query || null,
    metadata: options.metadata || null,
    user_id: userId,
    ...pageContext,
    ...tz,
    ...device,
    ...(activityType === "page_view" ? getAttributionOncePerSession() : {}),
  }));

  if (inserts.length === 0) return;

  try {
    // Record via server-side function (prevents public table writes)
    const { error } = await supabase.functions.invoke("track-guest-activity", {
      method: "POST",
      body: { events: inserts },
    });
    if (error) throw error;
  } catch (error) {
    // Classify and handle tracking errors appropriately
    const msg = String((error as any)?.message ?? error);
    const code = String((error as any)?.code ?? "");
    const lowerMsg = msg.toLowerCase();
    
    // Errors to suppress (expected/transient/environmental):
    const isExpectedError = (
      // Rate limiting - expected behavior
      lowerMsg.includes("rate_limit_exceeded") || code === "P0001" ||
      // Edge function transient failures (cold starts, network)
      lowerMsg.includes("edge function returned a non-2xx status code") ||
      lowerMsg.includes("functionshttperror") ||
      lowerMsg.includes("functionsrelayerror") ||
      lowerMsg.includes("functionsfetcherror") ||
      // Network issues - transient
      lowerMsg.includes("failed to fetch") ||
      lowerMsg.includes("load failed") ||
      lowerMsg.includes("failed to send a request") ||
      lowerMsg.includes("networkerror") ||
      lowerMsg.includes("network request failed") ||
      lowerMsg.includes("err_network") ||
      lowerMsg.includes("err_internet_disconnected") ||
      // Timeout - transient
      lowerMsg.includes("timeout") ||
      lowerMsg.includes("timed out") ||
      // CORS during navigation (user leaving page)
      lowerMsg.includes("cors") ||
      // AbortError (user navigating away)
      lowerMsg.includes("aborterror") ||
      lowerMsg.includes("aborted") ||
      // Service unavailable - transient
      lowerMsg.includes("503") ||
      lowerMsg.includes("502") ||
      lowerMsg.includes("504")
    );
    
    // Don't log expected/transient errors - they're not actionable
    if (isExpectedError) {
      // Silent fail for transient issues - don't pollute error log
      return;
    }
    
    // Log genuinely unexpected errors for investigation
    logError("tracking", `Guest activity tracking failed: ${msg}`, {
      errorCode: code,
      context: {
        activity_types: inserts.map(i => i.activity_type),
        count: inserts.length,
      },
    });
    
    console.error("Failed to track guest activity:", error);
  }
}

export async function trackGuestActivity(
  activityType: ActivityType,
  options: {
    flashcard_id?: string;
    chapter_id?: string;
    course_id?: string;
    search_query?: string;
    metadata?: Record<string, unknown>;
  } = {}
) {
  try {
    // Validate activity type
    if (!isValidActivityType(activityType)) {
      console.warn("Invalid activity type:", activityType);
      return;
    }

    // Create a unique key for this activity type + resource combination
    // For search, include the query to allow tracking different searches in quick succession
    // For quiz_learn, include set_id/item_id from metadata to differentiate events
    const metaSetId = (options.metadata as Record<string, unknown> | undefined)?.set_id as string | undefined;
    const metaItemId = (options.metadata as Record<string, unknown> | undefined)?.item_id as string | undefined;
    const resourceId = options.search_query || options.flashcard_id || options.chapter_id || options.course_id || metaItemId || metaSetId || 'none';
    const activityKey = `${activityType}:${resourceId}`;
    const now = Date.now();
    
    // Rate limiting: skip if this exact activity was tracked too recently
    if (lastActivityTime[activityKey] && now - lastActivityTime[activityKey] < RATE_LIMIT_MS) {
      return; // Skip - activity tracked too recently
    }
    
    lastActivityTime[activityKey] = now;

    // Add to queue
    trackingQueue.push({ activityType, options });

    // Debounce flush - use requestIdleCallback to avoid blocking INP
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    
    // Schedule flush during idle time to avoid blocking user interactions
    const scheduleFlush = () => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => { void flushTrackingQueue(); }, { timeout: 3000 });
      } else {
        void flushTrackingQueue();
      }
    };
    
    flushTimeout = setTimeout(() => {
      flushTimeout = null;
      scheduleFlush();
    }, 500);
  } catch (error) {
    // Silently fail - don't interrupt user experience for analytics
    console.error("Failed to queue guest activity:", error);
  }
}

export function getGuestSessionId() {
  return getSessionId();
}
